import {
  useEffect,
  useMemo,
  useState,
  useRef,
  Fragment,
  useCallback
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from 'store/store';
import {
  Categories,
  CategoryProducts,
  CategoryWithProducts,
  Products
} from 'types/bundle';

import {
  addShippingPrice,
  removeAdditionalProducts,
  removeShippingPrice,
  ServiceType,
  setSelectedServices
} from 'store/slices/selectedServices';

import {
  errorMessage,
  errorPlace,
  setErrorPlace
} from 'store/slices/errorSlice';

import { Badge, Collapse, Radio, RadioChangeEvent } from 'antd';
import Product from 'components/Product';
import ErrorMessage from 'components/ErrorMessage';
import { SelectNoneProduct } from './utils';

import {
  CheckedIconBlack,
  ExpandIconClosed,
  ExpandIconOpened
} from '../../../assets/Icons';
import { ReactComponent as WarningIcon } from 'assets/images/svg/error-icon.svg';
import GenericIcon from 'assets/images/svg/icon_generic.svg';

import style from './style.module.css';
import { setMandatoryProducts } from 'store/slices/shippingProductSlice';
import {
  INTERNET_CATEGORY_ID,
  PHONE_NUMBER_PRODUCT_ID,
  SHIPPING_PRODUCT_ID,
  TELEPHONE_CATEGORY_ID
} from 'constants/Constants';
import { setHasPortingOption } from 'store/slices/portingOptionSlice';
import { setCategoriesWithProduct } from 'store/slices/categoriesSlice';
import { setIsTelephoneSectionSeen } from 'store/slices/validationSlice';
import Message from 'components/Message';
import { MessageType } from 'components/Message/interface';

const { Panel } = Collapse;

const ProductsAndServices = () => {
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const dispatch: AppDispatch = useDispatch();

  const { data } = useSelector((state: RootState) => state.categories);

  const servicesData: ServiceType[] = useSelector(
    (state: RootState) => state.selectedServices.selectedProducts
  );

  const currentCategoriesWithProducts = useSelector(
    (state: RootState) => state.categories.categoriesWithProducts
  );

  const message = useSelector(errorMessage);
  const showError = useSelector(errorPlace);

  const [activeSection, setActiveSection] = useState<string>('1');

  const categories = data?.categories || [];
  const productsByCategory = data?.packages?.[0]?.products_by_category;

  const getCategoryById = (categoryId: number) => {
    return categories.find(
      (category: Categories) => category.id === categoryId
    );
  };

  const categoryProducts: CategoryProducts[] = useMemo(() => {
    if (productsByCategory) {
      return Object.values(productsByCategory);
    } else {
      return [];
    }
  }, [categories, productsByCategory]);

  const combinedProducts = useMemo(() => {
    return categoryProducts.flatMap(
      ({ additional_products, main_products }: any) => {
        const combinedArray: Products[] = [];

        if (additional_products) {
          combinedArray.push(...additional_products);
        }
        if (main_products) {
          combinedArray.push(...main_products);

          if (main_products.length > 1) {
            const category: Categories = getCategoryById(
              main_products?.[0]?.catID
            );
            combinedArray.push(SelectNoneProduct(category.id, category.name));
          }
        }
        return combinedArray;
      }
    );
  }, [categoryProducts]);

  useEffect(() => {
    const hasPortingOption = combinedProducts.some(
      (product) => product.productID === 729
    );

    dispatch(setHasPortingOption(hasPortingOption));
  }, []);

  const phoneNumberExists = useMemo(() => {
    const phoneProduct = combinedProducts.find(
      (prod) => prod.productID === PHONE_NUMBER_PRODUCT_ID
    );

    return !!phoneProduct;
  }, [combinedProducts]);

  useEffect(() => {
    if (!phoneNumberExists) dispatch(setIsTelephoneSectionSeen(true));
  }, [phoneNumberExists]);

  const getSelectedProductForCategory = (
    categoryProducts: Products[] | null | undefined
  ) => {
    if (!Array.isArray(categoryProducts)) return null;

    return categoryProducts.find(
      (product) =>
        Array.isArray(servicesData) &&
        servicesData.some(
          (selectedProduct) =>
            selectedProduct?.service?.productID === product.productID
        )
    );
  };

  const onChange = (key: string | string[]) => {
    const panelKey = Array.isArray(key) ? key[0] : key;
    setActiveSection(panelKey);
    scrollToPanel(panelKey);
  };

  const handleProductRadioChange = (e: RadioChangeEvent) => {
    const { value } = e.target;

    const selectedProduct: Products = combinedProducts?.find(
      (product: Products) => product?.productID === value
    ) as Products;
    let shippingProduct: Products | undefined = undefined;

    if (selectedProduct?.mandatory_products) {
      shippingProduct = combinedProducts?.find(
        (allProducts: Products) =>
          allProducts?.productID === selectedProduct?.mandatory_products[0]
      );
    }

    const categoryData: Categories = categories?.find(
      (category: Categories) => category?.id === selectedProduct?.catID
    );

    if (showError === categoryData?.name) {
      dispatch(setErrorPlace(''));
    }

    const index = servicesData.findIndex((product: ServiceType) => {
      return product?.service?.catID === selectedProduct?.catID;
    });

    if (index !== -1) {
      const updatedSelectedService = [...servicesData];
      updatedSelectedService.splice(index, 1, {
        service: selectedProduct,
        amount: 1
      });

      dispatch(setSelectedServices(updatedSelectedService));
    } else {
      dispatch(
        setSelectedServices([
          ...servicesData,
          { service: selectedProduct, amount: 1 }
        ])
      );
    }

    dispatch(removeAdditionalProducts());
    dispatch(removeShippingPrice());
    if (shippingProduct) {
      dispatch(addShippingPrice({ service: shippingProduct, amount: 1 }));
    }
  };

  useEffect(() => {
    const shippingProduct = combinedProducts.find(
      (product: Products) => product?.productID === SHIPPING_PRODUCT_ID
    );

    dispatch(setMandatoryProducts(shippingProduct as Products));
  }, [combinedProducts]);

  useEffect(() => {
    if (showError) {
      const activeCategory = categoriesWithProducts?.find(
        (categoryWithProducts: CategoryWithProducts) => {
          if (
            categoryWithProducts?.contains_products &&
            categoryWithProducts?.category?.user_selectable
          ) {
            return categoryWithProducts?.category?.name === showError;
          }
        }
      );

      setActiveSection(activeCategory?.category.id.toString() ?? '');
      window.scrollTo({ top: 20, left: 0, behavior: 'auto' });
    }
  }, [showError]);

  useEffect(() => {
    if (parseInt(activeSection) === TELEPHONE_CATEGORY_ID) {
      dispatch(setIsTelephoneSectionSeen(true));
      dispatch(setErrorPlace(''));
    }
  }, [activeSection]);

  const scrollToPanel = (key: string) => {
    setTimeout(() => {
      const panelRef = panelRefs.current[key];
      if (panelRef) {
        const panelTop = panelRef.getBoundingClientRect().top + window.scrollY;
        const offset = 200;
        window.scrollTo({
          top: panelTop - offset,
          behavior: 'smooth'
        });
      }
    }, 300);
  };

  const categoriesWithProducts: CategoryWithProducts[] = useMemo(() => {
    return categories.map((category: Categories) => {
      const productMatch: any = categoryProducts.find(
        (prod: any) =>
          (Array.isArray(prod?.main_products) &&
            prod.main_products.some(
              (p: Products) => p.catID === category.id
            )) ||
          (Array.isArray(prod?.additional_products) &&
            prod.additional_products.some(
              (p: Products) => p.catID === category.id
            )) ||
          (Array.isArray(prod?.related_additional_products) &&
            prod.related_additional_products.some(
              (p: Products) => p.catID === category.id
            ))
      );

      return {
        category: category,
        products: [
          {
            main_products: productMatch?.main_products ?? [],
            related_additional_products:
              productMatch?.related_additional_products ?? [],
            additional_products: productMatch?.additional_products ?? []
          }
        ],
        contains_products: !!productMatch
      };
    });
  }, [categories, categoryProducts]);

  useEffect(() => {
    if (
      JSON.stringify(categoriesWithProducts) !==
      JSON.stringify(currentCategoriesWithProducts)
    ) {
      dispatch(setCategoriesWithProduct(categoriesWithProducts));
    }
  }, [categoriesWithProducts, currentCategoriesWithProducts, dispatch]);

  const getDependantProducts = useCallback(
    (productId: number) => {
      return combinedProducts?.filter((prod) =>
        prod?.depends_on_products?.includes(productId)
      );
    },
    [combinedProducts]
  );

  const isProductIncluded = useCallback(
    (product: Products, dependantProducts: Products[]) => {
      return !!(
        !product.option ||
        ((dependantProducts?.length > 0 ||
          (product.depends_on_products?.length ?? 0) > 0) &&
          product.amount_included)
      );
    },
    []
  );

  const hasIncludedProducts = useCallback(
    (categoryId: number) => {
      return combinedProducts.some(
        (element: Products) =>
          element?.catID === categoryId &&
          isProductIncluded(element, getDependantProducts(element.productID))
      );
    },
    [combinedProducts]
  );

  const productHasDependencySelected = useCallback(
    (prod: Products) =>
      servicesData.some((product: ServiceType) =>
        prod?.depends_on_products?.includes(product?.service?.productID)
      ),
    [servicesData]
  );

  const isNotDependant = useCallback(
    (product: Products) => (product.depends_on_products?.length ?? 0) === 0,
    []
  );

  const sectionHeaderInAccordion = (text: string) => (
    <p className="package_name">{text}</p>
  );

  return (
    <Collapse
      onChange={onChange}
      accordion
      ghost
      bordered={true}
      expandIcon={({ isActive }) =>
        isActive ? <ExpandIconOpened /> : <ExpandIconClosed />
      }
      expandIconPosition="end"
      activeKey={activeSection}
    >
      {/************ MAIN PRODUCTS ************/}

      {categoriesWithProducts?.map(
        (categoryWithProducts: CategoryWithProducts) => {
          return (
            categoryWithProducts?.category?.user_selectable &&
            categoryWithProducts?.contains_products && (
              <Panel
                forceRender={hasIncludedProducts(
                  categoryWithProducts?.category?.id
                )}
                header={
                  <div className={style.collapse_header_wrapper}>
                    {servicesData.some(
                      (serviceProducts: ServiceType) =>
                        serviceProducts?.service?.catID ===
                        categoryWithProducts?.category?.id
                    ) ? (
                      <CheckedIconBlack />
                    ) : categoryWithProducts?.category?.image ? (
                      <img
                        src={categoryWithProducts?.category.image}
                        alt="category_icon"
                      />
                    ) : (
                      <img
                        src={GenericIcon}
                        alt="category_icon"
                        className={style.generic_icon}
                      />
                    )}
                    <h2 className={style.category_title}>
                      {categoryWithProducts?.category.name}
                    </h2>
                  </div>
                }
                key={categoryWithProducts.category.id}
                ref={(el) =>
                  (panelRefs.current[`${categoryWithProducts.category.id}`] =
                    el)
                }
              >
                <div>
                  {showError === categoryWithProducts?.category?.name && (
                    <ErrorMessage errorMessage={message} />
                  )}

                  {categoryWithProducts.category.id === TELEPHONE_CATEGORY_ID &&
                    phoneNumberExists && (
                      <Message
                        messageType={MessageType.WARNING}
                        icon={<WarningIcon />}
                      >
                        Bitte wählen Sie hier aktiv die gewünschte Anzahl an
                        Telefonnummern und zusätzlichen Gesprächskanälen. Diese
                        stehen im Standard auf 1, auch wenn eine größere Anzahl
                        kostenlos im gewählten Tarif enthalten ist:
                      </Message>
                    )}

                  {categoryWithProducts?.products &&
                    categoryWithProducts?.products?.map(
                      (products: CategoryProducts) => {
                        let categoryMainProductsWithNoneProduct =
                          products?.main_products;

                        if (categoryMainProductsWithNoneProduct?.length > 1) {
                          categoryMainProductsWithNoneProduct = [
                            ...categoryMainProductsWithNoneProduct,
                            SelectNoneProduct(
                              categoryWithProducts?.category?.id,
                              categoryWithProducts?.category?.name
                            )
                          ];
                        }

                        const selectedProduct = getSelectedProductForCategory(
                          categoryMainProductsWithNoneProduct
                        );

                        return (
                          <div
                            key={categoryWithProducts.category.id}
                            className={style.products_container}
                          >
                            <Radio.Group
                              onChange={handleProductRadioChange}
                              value={selectedProduct?.productID}
                            >
                              {categoryMainProductsWithNoneProduct?.map(
                                (prod: Products) => {
                                  const dependantProducts =
                                    getDependantProducts(prod.productID);

                                  const isIncluded = isProductIncluded(
                                    prod,
                                    dependantProducts
                                  );

                                  return (
                                    <div key={prod.productID}>
                                      {prod?.marker_type === 1 || isIncluded ? (
                                        <Badge.Ribbon
                                          text={
                                            <p
                                              className={
                                                prod?.marker_type === 1
                                                  ? style.text_black
                                                  : ''
                                              }
                                            >
                                              {prod?.marker_type === 1
                                                ? 'Kundenliebling'
                                                : 'Im Paket enthalten'}
                                            </p>
                                          }
                                          color={
                                            prod?.marker_type === 1
                                              ? '#ffff00'
                                              : '#181716'
                                          }
                                        >
                                          <Product
                                            product={prod}
                                            radio={true}
                                            dependantProducts={
                                              dependantProducts
                                            }
                                            isIncluded={isIncluded}
                                            isInternetProduct={
                                              prod.catID ===
                                              INTERNET_CATEGORY_ID
                                            }
                                          />
                                        </Badge.Ribbon>
                                      ) : (
                                        <Product
                                          product={prod}
                                          radio={true}
                                          dependantProducts={dependantProducts}
                                          isIncluded={isIncluded}
                                        />
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </Radio.Group>
                          </div>
                        );
                      }
                    )}
                </div>

                {/************ RELATED ADDITIONAL PRODUCTS ************/}
                {categoryWithProducts?.products &&
                  categoryWithProducts?.products?.map(
                    (products: CategoryProducts) => {
                      const visibleRelatedProducts =
                        products.related_additional_products?.filter(
                          (product) =>
                            isNotDependant(product) ||
                            productHasDependencySelected(product)
                        );

                      return (
                        <div
                          key={categoryWithProducts.category.id}
                          className={style.products_container}
                        >
                          {visibleRelatedProducts?.length > 0 && (
                            <div
                              className={style.additional_options_title_wrapper}
                            >
                              {sectionHeaderInAccordion('Weitere Optionen')}
                            </div>
                          )}
                          {visibleRelatedProducts?.map(
                            (relatedProduct: Products) => {
                              const isIncluded = isProductIncluded(
                                relatedProduct,
                                []
                              );

                              if (
                                isNotDependant(relatedProduct) ||
                                productHasDependencySelected(relatedProduct)
                              )
                                return (
                                  <div key={relatedProduct.productID}>
                                    {relatedProduct?.marker_type === 1 ||
                                    isIncluded ? (
                                      <Badge.Ribbon
                                        text={
                                          <p
                                            className={
                                              relatedProduct?.marker_type === 1
                                                ? style.text_black
                                                : ''
                                            }
                                          >
                                            {relatedProduct?.marker_type === 1
                                              ? 'Kundenliebling'
                                              : 'Im Paket enthalten'}
                                          </p>
                                        }
                                        color={
                                          relatedProduct?.marker_type === 1
                                            ? '#ffff00'
                                            : '#181716'
                                        }
                                      >
                                        <Product
                                          product={relatedProduct}
                                          checkbox={true}
                                          isIncluded={isIncluded}
                                          mainRelatedProducts={products.related_additional_products.map(
                                            (prod) => prod.productID
                                          )}
                                        />
                                      </Badge.Ribbon>
                                    ) : (
                                      <Product
                                        product={relatedProduct}
                                        checkbox={true}
                                        isIncluded={isIncluded}
                                        mainRelatedProducts={products.related_additional_products.map(
                                          (prod) => prod.productID
                                        )}
                                      />
                                    )}
                                  </div>
                                );
                            }
                          )}
                        </div>
                      );
                    }
                  )}

                {/************ ADDITIONAL PRODUCTS ************/}

                {categoryWithProducts?.products &&
                  categoryWithProducts?.products.map(
                    (products: CategoryProducts) => {
                      if (products?.additional_products?.length > 0) {
                        const {
                          additionalProductsDependantOnMain,
                          additionalProducts
                        } = products.additional_products.reduce(
                          (result: any, product: Products) => {
                            const isDependantOnMain =
                              product?.depends_on_products &&
                              product?.depends_on_products?.length > 0 &&
                              product.depends_on_products.some(
                                (dependantProductId) =>
                                  products?.main_products?.some(
                                    (mainProduct) =>
                                      mainProduct?.productID ===
                                      dependantProductId
                                  )
                              );
                            if (isDependantOnMain) {
                              result.additionalProductsDependantOnMain.push(
                                product
                              );
                            } else {
                              result.additionalProducts.push(product);
                            }

                            return result;
                          },
                          {
                            additionalProductsDependantOnMain: [],
                            additionalProducts: []
                          }
                        );

                        const showAdditional =
                          products.additional_products.some(
                            (product: Products) =>
                              product?.depends_on_products?.some((dependency) =>
                                servicesData.some(
                                  (serviceProduct: ServiceType) =>
                                    dependency ===
                                    serviceProduct?.service?.productID
                                )
                              )
                          );

                        if (products.additional_products.length > 0) {
                          return (
                            <Fragment key={categoryWithProducts.category.id}>
                              {showAdditional && (
                                <>
                                  <div
                                    className={
                                      style.additional_options_title_wrapper
                                    }
                                  >
                                    {sectionHeaderInAccordion('Zusatzprodukte')}
                                  </div>

                                  {/************ RENDER PRODUCTS WITH DEPENDENCIES ON MAIN PRODUCTS ************/}
                                  {additionalProductsDependantOnMain.map(
                                    (prod: Products) => {
                                      if (productHasDependencySelected(prod)) {
                                        const dependantProducts =
                                          getDependantProducts(prod.productID);

                                        const isIncluded = isProductIncluded(
                                          prod,
                                          dependantProducts
                                        );

                                        return (
                                          <div key={prod.productID}>
                                            {isIncluded ? (
                                              <Badge.Ribbon
                                                text={'Im Paket enthalten'}
                                                color="#181716"
                                              >
                                                <Product
                                                  product={prod}
                                                  checkbox={true}
                                                  dependantProducts={
                                                    dependantProducts
                                                  }
                                                  isIncluded={isIncluded}
                                                />
                                              </Badge.Ribbon>
                                            ) : (
                                              <Product
                                                product={prod}
                                                checkbox={true}
                                                dependantProducts={
                                                  dependantProducts
                                                }
                                                isIncluded={isIncluded}
                                              />
                                            )}
                                          </div>
                                        );
                                      }
                                    }
                                  )}
                                </>
                              )}

                              {/************ RENDER PRODUCTS NON DEPENDANT ON MAIN ************/}
                              {additionalProducts.length > 0 && (
                                <>
                                  {!showAdditional && (
                                    <div
                                      className={
                                        style.additional_options_title_wrapper
                                      }
                                    >
                                      {sectionHeaderInAccordion(
                                        'Zusatzprodukte'
                                      )}
                                    </div>
                                  )}
                                  {additionalProducts.map((prod: Products) => {
                                    if (
                                      (prod?.depends_on_products?.length ??
                                        0) === 0 ||
                                      prod?.depends_on_products?.some(
                                        (dependency) =>
                                          servicesData.some(
                                            (serviceProduct: ServiceType) =>
                                              dependency ===
                                              serviceProduct?.service?.productID
                                          )
                                      )
                                    ) {
                                      const dependantProducts =
                                        getDependantProducts(prod.productID);

                                      const isIncluded = isProductIncluded(
                                        prod,
                                        dependantProducts
                                      );

                                      return (
                                        <div key={prod.productID}>
                                          {isIncluded ? (
                                            <Badge.Ribbon
                                              text={'Im Paket enthalten'}
                                              color="#181716"
                                            >
                                              <Product
                                                product={prod}
                                                checkbox={true}
                                                dependantProducts={
                                                  dependantProducts
                                                }
                                                isIncluded={isIncluded}
                                              />
                                            </Badge.Ribbon>
                                          ) : (
                                            <Product
                                              product={prod}
                                              checkbox={true}
                                              dependantProducts={
                                                dependantProducts
                                              }
                                              isIncluded={isIncluded}
                                            />
                                          )}
                                        </div>
                                      );
                                    }
                                  })}
                                </>
                              )}
                            </Fragment>
                          );
                        }
                      }
                      return null;
                    }
                  )}
              </Panel>
            )
          );
        }
      )}
    </Collapse>
  );
};

export default ProductsAndServices;
